import React from 'react'
import { useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'
import { EColor } from '@frontend/pole-ui/lib/common/constants'
import { CommonLink } from '@frontend/pole-ui/lib/components/CommonLink'
import { Button } from '@frontend/ui-kit/agromarket/components/Button'
import { sendEmailForCallback } from '@pole-api/lib-ifa-communication-api/lib/clients/sendEmailForCallback'
import { useMutation } from '@tanstack/react-query'
import { MAX_LENGTH_NAME_FILED } from '@/components/composed/modals/FinancingCallbackModal/components/FinancingCallbackForm/constants'
import { Checkbox, Dropdown, TextInput } from '@/components/ui/ReactHookFormFields'
import { uncheckedRule } from '@/components/ui/ReactHookFormFields/Checkbox/helpers'
import { useData } from '@/logic/data'
import { getLegalDocumentUrl } from '@/logic/legalDocuments/helpers'
import { pushGtmEventOnSubmitCallbackForm } from '@/logic/metrika/financing/gtmHelpers'
import { getUserGtmInfo } from '@/logic/metrika/helpers'
import { getProfile } from '@/logic/profile/reducer'
import type { RootState } from '@/redux/interfaces'
import type { TLegalDocuments } from '@/types/legalDocuments'
import { ELegalDocumentType } from '@/types/legalDocuments'
import { formatLocalizedString } from '@/utils/formatUtils'
import { FinancingCallbackWrapper } from '../FinancingCallbackWrapper'
import { getDateOptions, getRequestBodyForCallbackRequest, getTimeIntervals } from './helpers'
import { messages } from './messages'
import type { IFinancingCallbackForm, IFinancingCallbackFormProps } from './types'
import './styles.scss'

const mapState = (state: RootState) => ({
  userGtmInfo: getUserGtmInfo()(state),
  profile: getProfile()(state),
})

export const FinancingCallbackForm = (props: IFinancingCallbackFormProps) => {
  const { onSuccessCallbackRequest, onErrorCallbackRequest, context } = props

  const { profile, userGtmInfo } = useSelector(mapState)

  const { control, handleSubmit, formState, watch } = useForm<IFinancingCallbackForm>({
    defaultValues: { phone: profile.phone, agreement: false },
  })
  const { isValid } = formState
  const watchDateFiled = watch('date')
  const { data: legalDocuments = [] } = useData<TLegalDocuments>('legalDocuments')

  const { mutateAsync: sendCallbackRequest, isLoading } = useMutation({ mutationFn: sendEmailForCallback })

  const onSubmitForm = async (formData: IFinancingCallbackForm) => {
    try {
      const requestBody = getRequestBodyForCallbackRequest(formData, context)
      await sendCallbackRequest(requestBody)

      pushGtmEventOnSubmitCallbackForm(userGtmInfo, 'success')

      onSuccessCallbackRequest()
    } catch (error) {
      onErrorCallbackRequest()
      pushGtmEventOnSubmitCallbackForm(userGtmInfo, 'fail')
    }
  }

  const onInvalid = () => {
    pushGtmEventOnSubmitCallbackForm(userGtmInfo, 'fail')
  }

  return (
    <FinancingCallbackWrapper>
      <div className="financingCallbackForm">
        <h4 className="financingCallbackForm__title">{messages.title}</h4>
        <div className="financingCallbackForm__subtitle">{messages.subtitle}</div>

        <form onSubmit={handleSubmit(onSubmitForm, onInvalid)} className="financingCallbackForm__form">
          <div className="financingCallbackForm__fields">
            <TextInput
              control={control}
              name="name"
              label={messages.form.name.label}
              rules={{
                maxLength: {
                  value: MAX_LENGTH_NAME_FILED,
                  message: formatLocalizedString(messages.form.errors.maxLength, { length: MAX_LENGTH_NAME_FILED }),
                },
              }}
            />
            <TextInput
              control={control}
              name="phone"
              type="tel"
              shouldBeTouchedBeforeError={false}
              label={messages.form.phone.label}
              isRequired
              rules={{ required: messages.form.errors.require }}
            />

            <div className="financingCallbackForm__fields-row">
              <Dropdown
                containerClassName="financingCallbackForm__fields-dropdown"
                control={control}
                name="date"
                options={getDateOptions()}
                label={messages.form.date.label}
              />
              <Dropdown
                containerClassName="financingCallbackForm__fields-dropdown"
                control={control}
                name="time"
                options={getTimeIntervals()}
                label={messages.form.time.label}
                isDisabled={!watchDateFiled}
                maxHeight={200}
              />
            </div>
          </div>

          <div className="financingCallbackForm__requiredInfo">{messages.form.requiredInfo}</div>

          <div className="financingCallbackForm__agreement">
            <Checkbox
              control={control}
              activeControlStyle={EColor.GREEN}
              borderColorStyle={EColor.LIGHT_GRAY}
              markColor="white"
              name="agreement"
              id="agreement"
              labelTextClassName="financingCallbackForm__agreement-label"
              rules={{ validate: uncheckedRule(messages.form.errors.require) }}
            >
              <div>
                {formatLocalizedString(messages.form.agreement.text, {
                  agreement: (
                    <CommonLink
                      key="registrationPolicy"
                      url={getLegalDocumentUrl(legalDocuments, ELegalDocumentType.personalDataConsent)}
                      shouldOpenInNewWindow
                      className="underline underline_green color_green"
                    >
                      {messages.form.agreement.agreement}
                    </CommonLink>
                  ),
                  policy: (
                    <CommonLink
                      key="registrationRules"
                      url={getLegalDocumentUrl(legalDocuments, ELegalDocumentType.registrationPolicy)}
                      shouldOpenInNewWindow
                      className="underline underline_green color_green"
                    >
                      {messages.form.agreement.policy}
                    </CommonLink>
                  ),
                })}
              </div>
            </Checkbox>
          </div>
          <div className="financingCallbackForm__actions">
            <Button className="financingCallbackForm__action" type="submit" isDisabled={isLoading || !isValid} isLoading={isLoading}>
              {messages.form.actions.submit}
            </Button>
          </div>
        </form>
      </div>
    </FinancingCallbackWrapper>
  )
}
